import { useEffect, useId, useRef, useState, type CSSProperties } from "react";

const getMatchScoreValue = (value: number | null | undefined) =>
  typeof value === "number" ? Math.max(0, Math.min(100, value)) : 0;

const getMatchScoreColors = (value: number) => {
  if (value >= 80) {
    return { start: "#1fb365", end: "#8fe05a" };
  }
  if (value >= 70) {
    return { start: "#f08a2a", end: "#ffd36a" };
  }
  if (value >= 60) {
    return { start: "#f4a81c", end: "#f7e45b" };
  }
  return { start: "#d63b39", end: "#f56b5d" };
};

export const MatchScoreRing = ({
  value,
  size = "small",
  sizePx,
}: {
  value: number | null | undefined;
  size?: "small" | "large";
  sizePx?: number;
}) => {
  const score = getMatchScoreValue(value);
  const showScore = typeof value === "number";
  const colors = getMatchScoreColors(score);
  const gradientId = useId();
  const sizeValue = sizePx ?? (size === "large" ? 78 : 46);
  const thickness =
    sizePx !== undefined
      ? Math.max(10, Math.round(sizeValue * (10 / 78)))
      : size === "large"
        ? 10
        : 7;
  const radius = (sizeValue - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const [animatedScore, setAnimatedScore] = useState(0);
  const animatedScoreRef = useRef(0);

  useEffect(() => {
    let frame = 0;
    const durationMs = 1200;
    const startValue = animatedScoreRef.current;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = Math.min(durationMs, now - startTime);
      const progress = durationMs > 0 ? elapsed / durationMs : 1;
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + (score - startValue) * eased;
      animatedScoreRef.current = nextValue;
      setAnimatedScore(nextValue);
      if (elapsed < durationMs) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [score]);

  const progress = Math.max(0, Math.min(1, animatedScore / 100));
  const dashOffset = circumference * (1 - progress);

  return (
    <div
      className={`matches-score-ring ${size}`}
      style={
        {
          "--size": `${sizeValue}px`,
          "--thickness": `${thickness}px`,
          "--ring-start": colors.start,
          "--ring-end": colors.end,
        } as CSSProperties
      }
    >
      <svg
        viewBox={`0 0 ${sizeValue} ${sizeValue}`}
        className="matches-score-ring-svg"
        role="img"
        aria-label={`Match score ${score}%`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.start} />
            <stop offset="100%" stopColor={colors.end} />
          </linearGradient>
        </defs>
        <circle
          className="matches-score-ring-track"
          cx={sizeValue / 2}
          cy={sizeValue / 2}
          r={radius}
          strokeWidth={thickness}
        />
        <circle
          className="matches-score-ring-progress"
          cx={sizeValue / 2}
          cy={sizeValue / 2}
          r={radius}
          strokeWidth={thickness}
          stroke={`url(#${gradientId})`}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <span>{showScore ? `${Math.round(animatedScore)}%` : ""}</span>
    </div>
  );
};
